import { Share2, Target, TrendingUp, ArrowRight, Users, Megaphone } from "lucide-react";
import { IndustryPageLayout } from "@/components/IndustryPageLayout";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

type IndustryImage = Tables<'industry_images'>;

const MarketingPage = () => {
  const [industryImages, setIndustryImages] = useState<IndustryImage[]>([]);

  useEffect(() => {
    const fetchIndustryImages = async () => {
      const { data, error } = await supabase
        .from('industry_images')
        .select('*')
        .eq('industry', 'marketing')
        .order('display_order', { ascending: true });

      if (error) {
        console.error('Error fetching industry images:', error);
      } else {
        setIndustryImages(data || []);
      }
    };

    fetchIndustryImages();
  }, []);

  const industry = {
    title: "Marketing",
    icon: Share2,
    gradient: "from-blue-500 to-cyan-500",
    description: "Create compelling ad visuals, social media content, and campaign materials that drive engagement and conversions at scale.",
    stats: "3x conversion rate"
  };

  const heroContent = (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Target className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-white mb-2">Targeted Campaigns</h3>
          <p className="text-gray-300 text-xs">Personalized visuals for every audience</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <Megaphone className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-white mb-2">Brand Amplification</h3>
          <p className="text-gray-300 text-xs">Consistent messaging across channels</p>
        </CardContent>
      </Card>
      <Card className="bg-white/5 backdrop-blur-md border-white/10">
        <CardContent className="p-6 text-center">
          <TrendingUp className="w-8 h-8 text-blue-400 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-white mb-2">Performance Boost</h3>
          <p className="text-gray-300 text-xs">Higher engagement and conversions</p>
        </CardContent>
      </Card>
    </div>
  );

  const imageGallery = (
    <div>
      <h3 className="text-xl font-bold text-white text-center mb-6">AI-Generated Marketing Examples</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industryImages.length > 0 ? (
          industryImages.map((image) => (
            <Card key={image.id} className="bg-white/5 backdrop-blur-md border-white/10 overflow-hidden group hover:bg-white/10 transition-all duration-300">
              <div className="aspect-square relative overflow-hidden">
                <img 
                  src={image.image_url} 
                  alt={image.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
              <CardContent className="p-4">
                <p className="text-sm text-gray-300">{image.title}</p>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="col-span-3 text-center text-gray-400 py-12">
            <Share2 className="w-12 h-12 mx-auto mb-4" />
            <p className="text-white">No marketing examples available yet</p>
            <p className="text-sm text-gray-400 mt-2">Images will be added soon</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <IndustryPageLayout
      industry={industry}
      seo={{
        title: "AI Marketing Visuals Generator | Campaign Images at Scale",
        description: "Create compelling marketing visuals with AI. Generate ad creatives, social media graphics, and campaign materials at scale. Boost engagement and conversions.",
        keywords: "marketing ai images, campaign visuals, advertising images ai, social media graphics, ad creatives, marketing automation",
        url: "/industries/marketing"
      }}
      heroContent={heroContent}
      mainContent={
        <div className="prose prose-invert max-w-none">
          <h2 className="text-3xl font-bold text-white mb-6">Transforming Marketing Campaigns</h2>
          
          <div className="text-gray-300 space-y-6 text-lg leading-relaxed">
            <p>
              In today's fast-paced marketing landscape, visual content is the key to capturing attention and driving conversions. AI-generated imagery enables marketers to create compelling, on-brand visuals at scale, reducing costs while maximizing impact.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">The Marketing Challenge</h3>
            <p>
              Modern marketing demands constant content creation across multiple channels, audiences, and campaigns. Traditional creative processes can't keep up with the volume and speed required for effective digital marketing.
            </p>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">AI-Powered Marketing Solutions</h3>
            <ul className="list-disc list-inside space-y-3 text-gray-300">
              <li><strong className="text-white">Ad Creative Generation:</strong> Create multiple ad variations for A/B testing</li>
              <li><strong className="text-white">Social Media Content:</strong> Generate platform-specific visuals at scale</li>
              <li><strong className="text-white">Campaign Personalization:</strong> Tailor visuals for different audience segments</li>
              <li><strong className="text-white">Brand Consistency:</strong> Maintain visual identity across all channels</li>
              <li><strong className="text-white">Rapid Prototyping:</strong> Test concepts quickly before major campaigns</li>
            </ul>
            
            <h3 className="text-2xl font-semibold text-white mt-8 mb-4">Proven Results</h3>
            <p>
              Marketing teams using AI-generated visuals report 3x higher conversion rates, 50% reduction in creative costs, and 70% faster campaign deployment. The ability to rapidly test and iterate visual concepts has revolutionized campaign optimization.
            </p>
            
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-6 mt-8">
              <h4 className="text-xl font-semibold text-blue-400 mb-3 flex items-center">
                <Share2 className="w-5 h-5 mr-2" />
                Amplify Your Marketing Impact
              </h4>
              <p className="text-gray-300">
                Create compelling marketing visuals that capture attention, drive engagement, and convert prospects into customers.
              </p>
            </div>
          </div>
        </div>
      }
      imageGallery={imageGallery}
    />
  );
};

export default MarketingPage;
